/**
 * «Поддержка партнёров» в плавающем «+» HUB: чистые функции подписей — срок ответа,
 * способ определения отдела, мета-строка «Мои обращения». Копия нужных функций из
 * frontend/src/lib/support-format.ts (импорт между пакетами запрещён); без React и
 * без запросов — проверяет vitest (support-format.test.ts). Цвета здесь не живут.
 */

const MSK = 'Europe/Moscow';

const MSK_PARTS = new Intl.DateTimeFormat('ru-RU', {
  timeZone: MSK,
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** «20.10, 13:00» по Москве (браузер может быть в любом поясе). */
export function formatMskDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p: Record<string, string> = {};
  for (const part of MSK_PARTS.formatToParts(d)) p[part.type] = part.value;
  return `${p.day}.${p.month}, ${p.hour}:${p.minute}`;
}

/** «до 20.10, 13:00» — срок ответа партнёру. */
export function formatDueUntil(iso: string | null | undefined): string {
  const t = formatMskDateTime(iso);
  return t === '—' ? '—' : `до ${t}`;
}

// ───────── Как определили отдел ─────────

export interface MethodPill {
  label: string;
  /** words — оранжевая плашка, ai — синяя, plain — серая. */
  tone: 'words' | 'ai' | 'plain';
}

const quoted = (words: string[]): string => words.map((w) => `«${w}»`).join(', ');

/** Плашка способа рядом с заголовком «Кому уйдёт вопрос»: «по словам», «ИИ по смыслу · 78%», … */
export function routingMethod(routedBy: string | null | undefined, ai?: { confidence: number } | null): MethodPill | null {
  switch (routedBy) {
    case 'keywords':
      return { label: 'по словам', tone: 'words' };
    case 'ai':
      return { label: ai ? `ИИ по смыслу · ${ai.confidence}%` : 'ИИ по смыслу', tone: 'ai' };
    case 'manual':
      return { label: 'выбрано вручную', tone: 'plain' };
    case 'fallback':
      return { label: 'по умолчанию', tone: 'plain' };
    default:
      return null;
  }
}

/** Строка «Почему» под формой вопроса. */
export function routingWhy(r: { routedBy: string; hits: string[]; ai?: { confidence: number; reason: string } | null }): string {
  switch (r.routedBy) {
    case 'keywords':
      return r.hits.length ? `нашли ${quoted(r.hits)}` : 'нашли подсказки отдела';
    case 'ai':
      return r.ai?.reason || 'по смыслу вопроса';
    case 'manual':
      return 'вы выбрали сами';
    default:
      return 'слов и смысла не хватило — отдел по умолчанию';
  }
}

/** Строка «Ответит»: человек и почему он, иначе руководитель отдела распределит. */
export function routingAssignee(a: { name: string; why: string } | null | undefined): string {
  return a ? (a.why ? `${a.name} — ${a.why}` : a.name) : 'руководитель отдела распределит';
}

/** Строка «Срок ответа» в форме: дата МСК, у срочного — «в течение часа — срочно». */
export function previewDueText(r: { urgent: boolean; slaMinutes: number; dueAt: string }): string {
  if (r.urgent && r.slaMinutes <= 60) return 'в течение часа — срочно';
  const due = formatDueUntil(r.dueAt);
  return r.urgent ? `${due} — срочно` : due;
}

/** Текст у кнопки «Отправить»: «Ответ до 20.10, 13:00» / «Срочно — ответим в течение часа». */
export function previewFooterText(r: { urgent: boolean; slaMinutes: number; dueAt: string }): string {
  if (r.urgent) return r.slaMinutes <= 60 ? 'Срочно — ответим в течение часа' : `Срочно — ответим ${formatDueUntil(r.dueAt)}`;
  return `Ответ ${formatDueUntil(r.dueAt)}`;
}

/** Фраза на экране «Спасибо!»: «Ответим до 20.10, 13:00.» / «Срочный вопрос — ответим в течение часа.» */
export function sentDueText(r: { urgent: boolean; dueAt: string | null; slaMinutes?: number | null }): string {
  if (!r.dueAt) return '';
  if (r.urgent && (r.slaMinutes ?? Infinity) <= 60) return 'Срочный вопрос — ответим в течение часа.';
  return `${r.urgent ? 'Срочный вопрос — ответим' : 'Ответим'} ${formatDueUntil(r.dueAt)}.`;
}

// ───────── «Мои обращения» у вопроса в УК ─────────

export interface AuthorQuestionFields {
  kind: string;
  status: string;
  deptLabel?: string | null;
  assigneeName?: string | null;
  taken?: boolean;
  dueAt?: string | null;
  firstResponseAt?: string | null;
  urgent?: boolean;
}

/**
 * Мета-строка вопроса для партнёра: отдел · «Взял(а) Имя» / «ещё не взяли» · «ответ до …».
 * У закрытого вопроса (Готово / Отклонено) и у остальных видов обращений — пусто.
 */
export function authorQuestionMeta(t: AuthorQuestionFields): { parts: string[]; urgent: boolean } {
  if (t.kind !== 'question') return { parts: [], urgent: false };
  const closed = t.status === 'done' || t.status === 'rejected';
  const parts: string[] = [];
  if (t.deptLabel) parts.push(t.deptLabel);
  if (!closed) {
    if (t.taken) parts.push(t.assigneeName ? `Взял(а) ${t.assigneeName}` : 'Взяли в работу');
    else parts.push('ещё не взяли');
    if (t.dueAt && !t.firstResponseAt) parts.push(`ответ ${formatDueUntil(t.dueAt)}`);
  }
  return { parts, urgent: !!t.urgent && !closed };
}

/** Длина текста и темы для определения отдела — как у бэкенда (ROUTE_TEXT_MAX и s(title, 200)). */
const ROUTE_TEXT_MAX = 5000;
const ROUTE_SUBJECT_MAX = 200;

/**
 * Текст для определения отдела: «тема\nтекст» (без темы — только текст), каждая часть обрезана по краям.
 * Бэкенд при создании вопроса собирает ТОТ ЖЕ текст (`routingTextOf` в support-routing.ts) — отдел, слова и
 * кэш ИИ совпадают с тем, что партнёр видел в предпросмотре. Менять обе стороны вместе.
 */
export function routingTextOf(subject: string | null | undefined, body: string): string {
  return [(subject ?? '').trim().slice(0, ROUTE_SUBJECT_MAX), body.trim()].filter(Boolean).join('\n').slice(0, ROUTE_TEXT_MAX);
}
